import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, of } from 'rxjs';
import { skip } from 'rxjs/operators';
import { AiUsageDimension } from 'src/app/enums/aiUsageDimension.enum';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { AiUsageGridRow } from 'src/app/models/aiUsageByUser.model';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { AuthService } from 'src/app/services';
import { AI_USAGE_GROUP_BY_FILTER_KEY, AI_USAGE_MONTH_FILTER_KEY, AiUsageByUserGridService } from 'src/app/services/aiUsageByUserGrid.service';
import { CommonService } from 'src/app/services/common.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { ToastService } from 'src/app/services/toast.service';
import { getAiAnalystAdminRouteConfig } from 'src/app/shared/constants/aiAnalystAdminRouteConfig.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { Filter } from 'src/app/shared/grid/baseGrid.component';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridRowTree, GridRowTreeMeta } from 'src/app/shared/grid/core-grid/gridRowTree.model';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { AiUsageByUserState } from './ai-usage-by-user.state';
import { buildExportRows, describeChange, formatChange, formatSites, isGroupRow, rowLabel } from './ai-usage-by-user.util';
import { AiUsageByUserToolbarComponent } from './ai-usage-by-user-toolbar/ai-usage-by-user-toolbar.component';

const SHARE_BAR_STEPS = 20;
const DEFAULT_LOCALE = 'en';
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CREDITS_NUMBER_FORMAT = '#,##0.00';
const PERCENT_NUMBER_FORMAT = '0%';
const SIGNED_PERCENT_NUMBER_FORMAT = '+0%;-0%;0%';
const DATE_TIME_NUMBER_FORMAT = 'yyyy-mm-dd hh:mm';
const WALL_CLOCK_UTC_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";
const USAGE_COLUMN_DETAILS: ColumnDetails = {
  displayName: '',
  type: 'string',
  isNullable: true,
  isFilterable: false,
  isSortable: false,
  isDefault: true
};

@Component({
  standalone: false,
  selector: 'app-ai-usage-by-user-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './ai-usage-by-user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AiUsageByUserState, DecimalPipe]
})
export class AiUsageByUserListComponent extends CoreGridComponent<AiUsageGridRow> implements OnInit, AfterViewInit {

  constructor(
    usageGridService: AiUsageByUserGridService,
    private state: AiUsageByUserState,
    toastService: ToastService,
    dialog: MatDialog,
    commonService: CommonService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private dateFormatService: DateFormatService,
    private decimalPipe: DecimalPipe,
    private translateService: TranslateService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = usageGridService;
    this.toolbarComponent = AiUsageByUserToolbarComponent;
    this.canRead = true;
    this.addDisabled = true;
    this.editDisabled = true;
    this.deleteDisabled = true;
  }

  protected get objectId(): string {
    return RolePermissionObjects.AiUsageRecord;
  }

  override get hasOrgFilter(): boolean {
    return true;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  protected override get showFilterControls(): boolean {
    return false;
  }

  protected override get showActionsColumn(): boolean {
    return false;
  }

  protected override get rowTree(): GridRowTree<AiUsageGridRow> {
    return {
      getChildren: row => row.children,
      isInitiallyExpanded: row => isGroupRow(row)
    };
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      combineLatest([this.state.month$, this.state.groupBy$]).pipe(skip(1)).subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.loadPage();
      })
    );
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  override getFilterBy(): Filter {
    return {
      ...super.getFilterBy(),
      [AI_USAGE_MONTH_FILTER_KEY]: this.state.month,
      [AI_USAGE_GROUP_BY_FILTER_KEY]: this.state.groupBy
    };
  }

  protected override onTreeRowsChanged(rows: AiUsageGridRow[], meta: Record<string, GridRowTreeMeta>): void {
    this.state.setVisibleRows(rows.map(row => ({ row, depth: meta[row.id]?.depth ?? 0 })));
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Usage';
    this.searchPlaceholder = 'Search user or email';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.name),
        displayName: 'User',
        isSortable: false,
        cssClass: row => isGroupRow(row) ? 'usage-group-row' : '',
        cellTemplate: row => this.nameCell(row)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.sites),
        displayName: 'Site',
        isSortable: false,
        cellTemplate: row => row.rowType === AiUsageDimension.User ? escapeHtml(formatSites(row.sites)) : ''
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.questionCount),
        displayName: 'Questions',
        isSortable: false,
        cellTemplate: row => String(row.questionCount)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.reportCount),
        displayName: 'Reports',
        isSortable: false,
        cellTemplate: row => String(row.reportCount)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.credits),
        displayName: 'Credits',
        isSortable: false,
        cellTemplate: row => this.decimalPipe.transform(row.credits, '1.2-2') ?? '-'
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.share),
        displayName: 'Share',
        isSortable: false,
        cellTemplate: row => this.shareCell(row)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.previousCredits),
        displayName: 'vs previous month',
        isSortable: false,
        cellTemplate: row => this.changeCell(row)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.activeDays),
        displayName: 'Active days',
        isSortable: false,
        cellTemplate: row => isGroupRow(row) ? '' : String(row.activeDays)
      }),
      new ColumnConfig<AiUsageGridRow>({
        name: nameOf<AiUsageGridRow>(x => x.lastActive),
        displayName: 'Last active',
        isSortable: false,
        cellTemplate: row => row.lastActive && !isGroupRow(row)
          ? this.dateFormatService.formatDateTimeWithTimeZoneToString(new Date(row.lastActive), this.dateFormatService.userDateFormat)
          : ''
      }),
      { name: 'action', displayName: 'Actions' }
    ];
    this.columnDetails = Object.fromEntries(this.columnConfigs.map(column => [column.name, USAGE_COLUMN_DETAILS]));
  }

  private toUserWallClock(date: Date): Date {
    return new Date(this.dateFormatService.formatDateTimeWithTimeZoneToString(date, WALL_CLOCK_UTC_FORMAT));
  }

  private get locale(): string {
    return this.translateService.currentLang || DEFAULT_LOCALE;
  }

  private nameCell(row: AiUsageGridRow): string {
    const label = escapeHtml(rowLabel(row, this.translateService.instant('Unknown user'), this.locale));
    if (!isGroupRow(row) || row.userCount === null) {
      return label;
    }
    const count = this.translateService.instant(row.userCount === 1 ? '{{count}} user' : '{{count}} users', { count: row.userCount });
    return `${label} <span class="usage-count">&middot; ${escapeHtml(count)}</span>`;
  }

  private shareCell(row: AiUsageGridRow): string {
    if (row.share === null) {
      return '';
    }
    const percent = Math.round(row.share * 100);
    const step = Math.min(SHARE_BAR_STEPS, Math.max(0, Math.round(row.share * SHARE_BAR_STEPS)));
    return `<span class="usage-share"><span class="usage-share-bar"><span class="usage-share-fill usage-share-fill-${step}"></span></span>${percent}%</span>`;
  }

  private changeCell(row: AiUsageGridRow): string {
    const change = describeChange(row);
    const label = formatChange(change, this.translateService.instant('new'), credits =>
      this.translateService.instant('was {{amount}}', { amount: this.decimalPipe.transform(credits, '1.2-2') ?? '' }));
    return `<span class="usage-pill usage-pill-${change.tone}">${escapeHtml(label)}</span>`;
  }

  override async exportToExcel(): Promise<void> {
    const rows = buildExportRows(this.state.visibleRows, this.translateService.instant('Unknown user'), this.locale);
    if (!rows.length) {
      this.toastService.showInfo('Nothing to export', true);
      return;
    }
    try {
      const exceljs: typeof import('exceljs') & { default?: typeof import('exceljs') } = await import(/* webpackChunkName: "exceljs" */ 'exceljs');
      const { Workbook } = exceljs.default ?? exceljs;
      const workbook = new Workbook();
      const sheet = workbook.addWorksheet(this.translateService.instant('Usage by user'));
      sheet.columns = [
        { header: this.translateService.instant('User'), key: 'label', width: 36 },
        { header: this.translateService.instant('Email'), key: 'email', width: 32 },
        { header: this.translateService.instant('Site'), key: 'site', width: 28 },
        { header: this.translateService.instant('Questions'), key: 'questions', width: 12 },
        { header: this.translateService.instant('Reports'), key: 'reports', width: 12 },
        { header: this.translateService.instant('Credits'), key: 'credits', width: 14, style: { numFmt: CREDITS_NUMBER_FORMAT } },
        { header: this.translateService.instant('Share'), key: 'share', width: 10, style: { numFmt: PERCENT_NUMBER_FORMAT } },
        { header: this.translateService.instant('vs previous month'), key: 'change', width: 18, style: { numFmt: SIGNED_PERCENT_NUMBER_FORMAT } },
        { header: this.translateService.instant('Active days'), key: 'activeDays', width: 12 },
        { header: this.translateService.instant('Last active'), key: 'lastActive', width: 20, style: { numFmt: DATE_TIME_NUMBER_FORMAT } }
      ];
      sheet.getRow(1).font = { bold: true };
      rows.forEach(row => sheet.addRow({
        ...row,
        change: row.change === null ? null : row.change / 100,
        lastActive: row.lastActive ? this.toUserWallClock(row.lastActive) : null
      }));
      const buffer = await workbook.xlsx.writeBuffer();
      downloadBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), `ai-usage-by-user-${this.state.month}.xlsx`);
    } catch {
      this.toastService.displayErrorMessage('Export failed', true);
    }
  }

  override initializeTabs(): { routerLink: string; title: string; objectId?: string }[] {
    const currentUser = this.authService.getCurrentUser();
    return getAiAnalystAdminRouteConfig(currentUser.userType === SecurityProfileEnum.SuperAdmin, !!currentUser.isAiAnalystEnabled);
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override openEntityDialog(_entityId?: string): void {
    return;
  }

  override openEntityDeleteDialog(_row: AiUsageGridRow): void {
    return;
  }
}
